import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { ThemeProvider, useTheme } from '@emotion/react';
import * as timeUtil from '@chronos/time-util';
import ExternalLinkIcon from '../Icons/ExternalLinkIcon';
import CommentPopover from './CommentPopover';
import WorklogCommentForm from './WorklogCommentForm';
import * as S from './styled';
import AddCommentIcon from '../Icons/AddCommentIcon';
import { StopIcon } from '../Icons/StopIcon';

const SIZES_MAP = {
  small: 20,
  medium: 30,
  large: 35,
};

const TrackingBar = (props) => {
  const {
    onClickTrackingCardname,
    onClickEstimateModal,
    onStopTimer,
    onPlayPause,
    onSaveComment,
    hasMultipleTimers,
    isExpandCollapseVisible,
    isTrackerExpanded,
    setTrackerExpanded,
    showEstimates,
    showButtons,
    card,
    estimates,
    timerState,
    isExternal,
    mode,
    size,
    appearance = 'dark',
    isMobile,
  } = props;
  const appTheme = useTheme();

  const themeMapByAppearance = {
    light: {
      light: {
        color: appTheme.colors.primary,
        background: 'transparent',
      },
      dark: {
        color: appTheme.colors.primary,
        // background: '#182a4e',
        background: 'transparent',
      },
    },
    dark: {
      light: {
        background: '#172b4d',
        color: '#fff',
      },
      dark: {
        color: '#fff',
        background: '#172b4d',
      },
    },
  };
  const trackingBarTheme = themeMapByAppearance[appearance][appTheme.name];

  const totalEstimateString = useMemo(
    () => timeUtil.stj(timeUtil.getTotalEstimate(estimates)),
    [estimates]
  );
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const iconWidthBySize = {
    large: '30px',
    medium: '24px',
    small: '20px',
  };
  const commentIcon = (
    <AddCommentIcon
      size={[iconWidthBySize[size], iconWidthBySize[size]]}
      primaryColor="white"
    />
  );

  const openIcon = mode === 'pwa' ? '↑' : '↓';
  return (
    <ThemeProvider theme={{ trackingBar: trackingBarTheme }}>
      <S.BarBackground mode={mode}>
        <S.TrackingBar
          mode={mode}
          size={size}
        >
          {hasMultipleTimers && isExpandCollapseVisible && (
            <S.ExpandCollapse
              mode={mode}
              size={size}
              onClick={() => {
                setTrackerExpanded(!isTrackerExpanded);
              }}
            >
              {isTrackerExpanded ? openIcon : '→'}
            </S.ExpandCollapse>
          )}
          {isExternal && (
            <ExternalLinkIcon
              style={{
                fill: '#fff',
                cursor: 'pointer',
                minWidth: iconWidthBySize[size],
              }}
              onClick={() => {
                onClickTrackingCardname(card);
              }}
              size={[iconWidthBySize[size], iconWidthBySize[size]]}
            />
          )}
          {showButtons &&
            (mode === 'pwa' ? (
              <S.CommentIcon
                onClick={() => {
                  setIsCommentModalOpen(true);
                }}
              >
                {commentIcon}
              </S.CommentIcon>
            ) : (
              <CommentPopover
                icon={commentIcon}
                onSubmit={(value) => {
                  if (value !== timerState.trackingWorklogComment) {
                    onSaveComment(timerState, value);
                  }
                }}
                comment={timerState.trackingWorklogComment}
              />
            ))}
          <S.Timer mode={mode}>
            {showEstimates && (
              <div>
                <S.EstimateText
                  onClick={() => {
                    onClickEstimateModal(timerState.cardId);
                  }}
                >
                  {estimates.length ? `Est: ${totalEstimateString}` : 'No est.'}
                </S.EstimateText>
              </div>
            )}
            <S.CardName
              mode={mode}
              size={size}
              isLoading={card.id === undefined}
              onClick={() => {
                onClickTrackingCardname(card);
              }}
            >
              <span>{card.id ? card.name : 'Unknown card name'}</span>
            </S.CardName>
            <S.Dot />
            <S.Time size={size}>
              {timeUtil.getTimeString(timerState.timer)}
            </S.Time>
          </S.Timer>
          {showButtons && (
            <S.ControlButtons>
              {['run', 'pause'].includes(timerState.timerStatus) ? (
                <>
                  <S.PlayPauseButton
                    onClick={onPlayPause}
                    size={size}
                    isPlay={timerState.timerStatus === 'pause'}
                  />
                  <StopIcon
                    onClick={onStopTimer}
                    height={`${SIZES_MAP[size]}`}
                    width={`${SIZES_MAP[size]}`}
                    color={trackingBarTheme.color}
                  />
                </>
              ) : (
                <S.PlayPauseButton
                  onClick={onPlayPause}
                  size={size}
                  isPlay
                />
              )}
            </S.ControlButtons>
          )}
        </S.TrackingBar>
        {isCommentModalOpen && (
          <WorklogCommentForm
            isOpen
            isMobile
            title="Edit Worklog Comment"
            handleClose={() => {
              setIsCommentModalOpen(false);
            }}
            handleSubmit={(value) => {
              if (value !== timerState.trackingWorklogComment) {
                onSaveComment(value);
              }
            }}
            boardId={timerState.boardId}
            worklog={{ comment: timerState.trackingWorklogComment }}
          />
        )}
      </S.BarBackground>
    </ThemeProvider>
  );
};

TrackingBar.defaultProps = {
  card: {
    name: '',
  },
  estimates: [],
  onClickTrackingCardname: () => {},
  onClickEstimateModal: () => {},
  onStopTimer: () => {},
  onPlayPause: () => {},
  hasMultipleTimers: false,
  isExpandCollapseVisible: false,
  isTrackerExpanded: false,
  showEstimates: false,
  showButtons: true,
  isExternal: false,
  mode: 'pwa',
  theme: 'dark',
  size: 'large',
  isMobile: false,
};

TrackingBar.propTypes = {
  timerState: PropTypes.shape({
    cardId: PropTypes.string.isRequired,
    boardId: PropTypes.string.isRequired,
    timerStatus: PropTypes.string.isRequired,
    trackingWorklogComment: PropTypes.string.isRequired,
    timer: PropTypes.number.isRequired,
  }).isRequired,
  card: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    idBoard: PropTypes.string,
  }),
  estimates: PropTypes.array,
  onClickTrackingCardname: PropTypes.func,
  onClickEstimateModal: PropTypes.func,
  onStopTimer: PropTypes.func,
  onPlayPause: PropTypes.func,
  onSaveComment: PropTypes.func,
  setTrackerExpanded: PropTypes.func,
  mode: PropTypes.oneOf(['pwa', 'powerup']),
  hasMultipleTimers: PropTypes.bool,
  isExpandCollapseVisible: PropTypes.bool,
  isTrackerExpanded: PropTypes.bool,
  showEstimates: PropTypes.bool,
  showButtons: PropTypes.bool,
  isExternal: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  appearance: PropTypes.oneOf(['light', 'dark']),
  isMobile: PropTypes.bool,
};

export default TrackingBar;
